import React from "react";
import { DroppsProduct } from "../../store";
import {
  ShopifyCartItem,
  cartItemSelect,
  shopifyCartApi,
} from "../../services/shopifyCart";
import { Button } from "@/components/Button";
import CodeIcon from "@mui/icons-material/Code";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { productScentMapping } from "@/helpers/data/cartProductSwapVariants";
import { productSelect } from "@/store";
import { CircularProgress } from "@mui/material";

export const ProductSwapModule = ({
  allProductsMap,
  addedLineItem,
  type,
  text,
}: {
  allProductsMap: Map<number, DroppsProduct>;
  addedLineItem: ShopifyCartItem & { shopifyProduct?: DroppsProduct };
  type: "upgrade" | "downgrade";
  text: React.ReactNode;
}) => {
  const addItem = shopifyCartApi.useTrackedAdd();
  const extractedProductId = parseInt(
    typeof addedLineItem?.properties?.["_downgrade-product-id"] === "string"
      ? addedLineItem.properties["_downgrade-product-id"]
      : addedLineItem?.properties?.["_downgrade-product-id"]?.toString() ?? "0"
  );
  const extractedVariantId = parseInt(
    typeof addedLineItem?.properties?.["_downgrade-variant-id"] === "string"
      ? addedLineItem.properties["_downgrade-variant-id"]
      : addedLineItem?.properties?.["_downgrade-variant-id"]?.toString() ?? "0"
  );
  const productFromMapList = productScentMapping[addedLineItem.product_id];
  const swapProduct =
    type === "upgrade"
      ? allProductsMap.get(productFromMapList?.upgradeProductId)
      : allProductsMap.get(extractedProductId);
  const isSwapProductOrderable = swapProduct
    ? productSelect.orderable(swapProduct)
    : false;
  const swapVariant =
    type === "upgrade"
      ? productFromMapList?.variants[addedLineItem.variant_id]
      : extractedVariantId;
  const quantity = addedLineItem.quantity;
  const selectedSellingPlan = cartItemSelect.subscription(addedLineItem);
  let findSellingPlanId: number;
  if (
    selectedSellingPlan.kind === "selling-plan" &&
    swapProduct !== undefined
  ) {
    const getPlan = swapProduct.sellingPlans.find(
      (plan) => plan.name === selectedSellingPlan.name
    );
    if (getPlan !== undefined) {
      findSellingPlanId = getPlan.id;
    }
  }

  if (!isSwapProductOrderable && typeof swapVariant === "number") {
    return null;
  }

  const handleClick = async () => {
    const cartBody =
      type === "upgrade"
        ? {
            items: [
              {
                id: swapVariant,
                quantity,
                selling_plan: findSellingPlanId,
                properties: {
                  "_downgrade-variant-id": addedLineItem.variant_id,
                  "_downgrade-product-id": addedLineItem.product_id,
                },
              },
            ],
            sourceModule: "cart_product_upgrade",
          }
        : {
            items: [
              {
                id: swapVariant,
                quantity,
                selling_plan: findSellingPlanId,
              },
            ],
            sourceModule: "cart_product_downgrade",
          };
    await shopifyCartApi.change({ id: addedLineItem.key, quantity: 0 });
    addItem.mutate(cartBody);
  };

  return (
    <div className="relative px-4 pb-4">
      <div
        className={`flex items-center rounded-sm border border-solid px-3 py-2 ${
          type === "upgrade"
            ? "border-purple-200 bg-purple-50"
            : "border-secondary-300 bg-secondary-50"
        }`}
      >
        <div className="mr-3 text-right">
          <p className="text-xs">{text}</p>
        </div>
        <Button
          variant="outline"
          onClick={handleClick}
          className={`h-[26px] flex-shrink-0 rounded-full border-2 border-solid pb-0 pl-[2px] pr-[6px] pt-0 ${
            type === "upgrade"
              ? "border-purple-800 text-purple-800 not-disabled:hover:border-purple-800 not-disabled:hover:bg-purple-800"
              : "border-secondary-800 text-secondary-800 not-disabled:hover:border-secondary-800 not-disabled:hover:bg-secondary-800"
          }`}
        >
          {type === "upgrade" ? (
            <>
              <AddRoundedIcon className="text-md"></AddRoundedIcon>
              <span className="tracking-normal">Upgrade</span>
            </>
          ) : (
            <>
              <CodeIcon className="text-md"></CodeIcon>
              <span className="tracking-normal">Swap</span>
            </>
          )}
        </Button>
      </div>
    </div>
  );
};
